// Conversations — every conversation of this person, kept in step with my space.
//
// - A conversation is announced in MY space ('conv.add', readable by my devices only): that is
//   how my other devices learn its secrets. Re-announced whenever its key epoch changes, so a
//   device of mine added later still gets every epoch.
// - Reconciliation: when a device joins my space, one of my devices already in the conversation
//   adds it there (attested by my identity). When I revoke a device, the revoking device removes
//   it from every conversation and rotates their keys.
// - Groups (docs/phase-4-groupes.md): created among my contacts; a contact is invited through our
//   direct conversation (group.invite, which travels like any message); a departure is completed
//   (revocation + new key) by one device of the group's creator.
//
// Events: 'change' (list, messages or statuses changed).

import type { ConvRecord, Space } from '../space/space';
export type { ConvRecord };
import type { Payload } from '../space/envelope';
import { newSpace, publicRecord, type DeviceRecord, type SpaceSecrets } from '../space/keys';
import { IdbStore, type Store } from '../space/store';
import { SpaceSync } from '../space/sync';
import { revokeAndRekey, revokeManyAndRekey } from '../space/rekey';
import { Conversation, type Person } from './conversation';
import { attest, identityFrom, type Identity } from './identity';


export type ConversationsOptions = {
  storeFor?: (conv: string) => Store;
  /** Starts the network sync of a conversation; tests pass null to drive sync by hand. */
  syncFor?: ((conv: Conversation) => SpaceSync) | null;
  dropStore?: (conv: string) => Promise<void>;
};

export class Conversations extends EventTarget {
  readonly map = new Map<string, Conversation>();
  identity!: Identity;
  readonly #opts: Required<ConversationsOptions>;
  #tasks: Promise<void> = Promise.resolve();

  constructor(
    readonly space: Space,
    opts: ConversationsOptions = {},
  ) {
    super();
    this.#opts = {
      storeFor: opts.storeFor ?? ((conv) => new IdbStore(`conv-${conv}`)),
      syncFor: opts.syncFor === undefined ? null : opts.syncFor,
      dropStore: opts.dropStore ?? ((conv) => deleteDb(`conv-${conv}`)),
    };
  }

  get me(): Person {
    return { identity: this.identity.pub, name: this.space.displayName };
  }

  /** Changes my name for my devices and every contact. */
  async setMyName(name: string): Promise<void> {
    const clean = name.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, 60);
    if (!clean) return;
    await this.space.journal.append({ type: 'profile.set', ts: Date.now(), name: clean });
    for (const c of this.map.values()) await this.#reconcile(c);
  }

  /** My private nickname for a contact (null removes it); shared with my devices only. */
  async setAlias(person: string, name: string | null): Promise<void> {
    const clean = name?.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, 60) || null;
    await this.space.journal.append({ type: 'contact.alias', ts: Date.now(), person, name: clean });
  }

  async load(): Promise<void> {
    this.identity = await identityFrom(this.space.journal.space.secrets[0]);
    await this.space.convsSettled();
    for (const rec of this.space.convs.values()) if (!rec.closed) await this.#open(rec);
    for (const conv of this.map.values()) await this.#finishLeaving(conv);
    for (const conv of this.map.values()) await this.#reconcile(conv);
    // A name or nickname changed (here or on another device of mine): screens re-render.
    this.space.addEventListener('profile', this.#changed);
    this.space.addEventListener('conv', (e) => {
      const { rec, isNew } = (e as CustomEvent).detail as { rec: ConvRecord; isNew: boolean };
      if (rec.closed) return;
      this.#serial(async () => (isNew ? void (await this.#open(rec)) : this.#mergeEpochs(rec)));
    });
    // I left a group (here or on another device of mine): hide it now, close it once processed.
    this.space.addEventListener('conv-left', (e) => {
      this.#changed();
      const conv = this.map.get((e as CustomEvent).detail.conv);
      if (conv) this.#serial(() => this.#finishLeaving(conv));
    });
    this.space.journal.addEventListener('envelope', (e) => {
      const { payload } = (e as CustomEvent).detail as { payload: Payload };
      if (payload.type === 'device.add') this.#serial(async () => {
        for (const conv of this.map.values()) await this.#reconcile(conv);
      });
    });
    this.space.addEventListener('revoked', (e) => {
      const { device } = (e as CustomEvent).detail as { device: string };
      this.#serial(async () => {
        for (const c of this.map.values()) {
          if (c.journal.isMember && c.journal.members.has(device) && c.journal.members.get(device)?.revokedAfter === undefined) {
            await revokeAndRekey(c.journal, device);
            await c.sync?.refreshRooms();
            await this.#announce(c);
          }
        }
      });
    });
  }

  /** Resolves once announcements and reconciliations queued so far are done. */
  settled(): Promise<void> {
    return this.#tasks;
  }

  /** Conversations to show: groups I left are hidden while their departure finishes. */
  list(): Conversation[] {
    const since = (c: Conversation) => c.last()?.ts ?? this.space.convs.get(c.id)?.ts ?? 0;
    return [...this.map.values()].filter((c) => !this.space.convs.get(c.id)?.left).sort((a, b) => since(b) - since(a));
  }

  /** Host side of an invitation: creates the conversation with all my devices and theirs. */
  async createAsHost(peer: Person, theirDevices: DeviceRecord[]): Promise<SpaceSecrets> {
    const secrets = await newSpace();
    const rec: ConvRecord = { conv: secrets.spaceId, secrets, peer, ts: Date.now() };
    const conv = await this.#open(rec, { fresh: true });
    const self = await attest(this.identity, publicRecord(this.space.device));
    await conv.journal.append({ type: 'device.add', ts: Date.now(), device: self });
    for (const m of this.space.journal.activeMembers()) {
      if (m.record.id !== this.space.device.id) await conv.journal.append({ type: 'device.add', ts: Date.now(), device: await attest(this.identity, publicRecord(m.record)) });
    }
    for (const d of theirDevices) await conv.journal.append({ type: 'device.add', ts: Date.now(), device: d });
    await this.#announce(conv);
    return structuredClone(conv.journal.space);
  }

  /** My direct conversations, i.e. my contacts. */
  contacts(): Conversation[] {
    return this.list().filter((c) => c.kind === 'direct' && c.peer);
  }

  /** Creates a group with some of my contacts; I am its creator (administrator). */
  async createGroup(name: string, contacts: Conversation[]): Promise<Conversation> {
    const secrets = await newSpace();
    const clean = name.trim().slice(0, 80);
    const rec: ConvRecord = { conv: secrets.spaceId, secrets, kind: 'group', name: clean, ts: Date.now() };
    const group = await this.#open(rec, { fresh: true });
    await group.journal.append({ type: 'device.add', ts: Date.now(), device: await attest(this.identity, publicRecord(this.space.device)) });
    for (const m of this.space.journal.activeMembers()) {
      if (m.record.id !== this.space.device.id) await group.journal.append({ type: 'device.add', ts: Date.now(), device: await attest(this.identity, publicRecord(m.record)) });
    }
    if (clean) await group.rename(clean);
    await this.#announce(group);
    await this.addToGroup(group, contacts);
    return group;
  }

  /**
   * Any member adds some of their contacts: their devices (known and attested through our direct
   * conversation) join the group, and the invitation with the group's secrets goes through that
   * direct conversation — so it reaches them like a message, even if they are offline right now.
   */
  async addToGroup(group: Conversation, contacts: Conversation[]): Promise<void> {
    for (const c of contacts) {
      if (c.kind !== 'direct' || !c.peer) continue;
      for (const id of c.devicesOf(c.peer.identity)) {
        const record = c.journal.members.get(id)?.record;
        if (record && !group.journal.members.has(id)) await group.journal.append({ type: 'device.add', ts: Date.now(), device: record });
      }
      await c.journal.append({ type: 'group.invite', ts: Date.now(), group: group.id, secrets: structuredClone(group.journal.space), name: group.title() });
    }
  }

  /** Creator only (the journal policy refuses it from anyone else). */
  async removeFromGroup(group: Conversation, person: string): Promise<void> {
    await group.journal.append({ type: 'member.remove', ts: Date.now(), person });
    await revokeManyAndRekey(group.journal, group.devicesOf(person));
    await group.sync?.refreshRooms();
    await this.#announce(group);
  }

  /**
   * Leaves a group on all my devices. The group disappears from my list at once, but stays
   * connected in the background until my devices see their own removal (the creator's device
   * revokes them and rotates the key): only then has the departure surely reached the others.
   */
  async leaveGroup(group: Conversation): Promise<void> {
    await group.journal.append({ type: 'member.leave', ts: Date.now(), person: this.identity.pub });
    await this.space.journal.append({ type: 'conv.leave', ts: Date.now(), conv: group.id });
    await this.space.convsSettled();
    await this.settled();
  }

  /** Guest side: the host sent the secrets; the conversation reaches my devices through my space. */
  async joinAsGuest(secrets: SpaceSecrets, host: Person): Promise<Conversation> {
    await this.space.journal.append({ type: 'conv.add', ts: Date.now(), conv: secrets.spaceId, secrets, peer: host });
    await this.space.convsSettled();
    await this.settled();
    return this.map.get(secrets.spaceId)!;
  }

  /** My devices, attested, for an invitation hello. */
  async myDevices(): Promise<DeviceRecord[]> {
    return Promise.all(this.space.journal.activeMembers().map((m) => attest(this.identity, publicRecord(m.record))));
  }

  async stopAll(): Promise<void> {
    for (const c of this.map.values()) c.sync?.stop();
  }

  async deleteAll(): Promise<void> {
    await this.stopAll();
    for (const id of this.map.keys()) await this.#opts.dropStore(id);
    this.map.clear();
  }

  #serial(fn: () => Promise<void>): void {
    this.#tasks = this.#tasks.then(fn).catch((err) => console.error('conversations', err));
  }

  #changed = () => this.dispatchEvent(new Event('change'));

  /** A re-announcement carried key epochs (or concurrent secrets) this device's copy lacked. */
  async #mergeEpochs(rec: ConvRecord): Promise<void> {
    const conv = this.map.get(rec.conv);
    if (!conv) return void (await this.#open(rec));
    await this.#adoptAll(conv, rec.secrets);
    await conv.sync?.refreshRooms();
  }

  async #adoptAll(conv: Conversation, secrets: SpaceSecrets): Promise<void> {
    for (const [epoch, secret] of Object.entries(secrets.secrets)) {
      await conv.journal.adoptSecret(Number(epoch), secret, secrets.rekeyAuthors?.[Number(epoch)] ?? '\uffff');
    }
    for (const [epoch, alts] of Object.entries(secrets.alts ?? {})) {
      for (const alt of alts) await conv.journal.adoptSecret(Number(epoch), alt, '\uffff');
    }
  }

  /** A group I left: once my device is revoked there (departure processed), drop my copy. */
  async #finishLeaving(conv: Conversation): Promise<void> {
    const rec = this.space.convs.get(conv.id);
    if (!rec?.left || !conv.journal.isRevoked) return;
    conv.sync?.stop();
    this.map.delete(conv.id);
    await this.#opts.dropStore(conv.id);
    await this.space.markClosed(conv.id);
    this.#changed();
  }

  /** A contact invited me to a group (through our direct conversation): it joins my space. */
  async #onGroupInvite(from: Conversation, author: string, p: Extract<Payload, { type: 'group.invite' }>): Promise<void> {
    await from.settled();
    if (!from.peer || from.personOf(author) !== from.peer.identity) return;
    const known = this.space.convs.get(p.group);
    if (known && !known.left && !known.closed) return void (await this.#adoptAll(this.map.get(p.group) ?? (await this.#open(known)), p.secrets));
    await this.space.journal.append({ type: 'conv.add', ts: Date.now(), conv: p.group, secrets: p.secrets, kind: 'group', name: String(p.name).slice(0, 80) });
    await this.space.convsSettled();
  }

  /** Someone left a group I created: one device of mine (the smallest id) completes it. */
  async #onLeave(group: Conversation, person: string): Promise<void> {
    await group.settled();
    if (group.creator !== this.identity.pub || person === this.identity.pub) return;
    const mine = group.devicesOf(this.identity.pub).sort();
    if (mine[0] !== this.space.device.id) return;
    await revokeManyAndRekey(group.journal, group.devicesOf(person));
    await group.sync?.refreshRooms();
    await this.#announce(group);
  }

  async #open(rec: ConvRecord, { fresh = false } = {}): Promise<Conversation> {
    const existing = this.map.get(rec.conv);
    if (existing) return existing;
    const store = this.#opts.storeFor(rec.conv);
    if (fresh) await store.setKv('space', rec.secrets);
    const conv = new Conversation(store, this.space.device, structuredClone(rec.secrets), this.identity.pub, rec.peer ?? null, rec.kind ?? 'direct', rec.name ?? '');
    await conv.load();
    this.map.set(rec.conv, conv);
    conv.addEventListener('change', this.#changed);
    conv.aliasFor = (person) => this.space.aliases.get(person)?.name ?? null;
    // A rekey received inside the conversation is re-announced to my devices.
    conv.journal.addEventListener('rekey', () => this.#serial(() => this.#announce(conv)));
    // Becoming a member (my admission arrived) is the moment to add my other devices.
    conv.journal.addEventListener('envelope', (e) => {
      const { envelope, payload } = (e as CustomEvent).detail as { envelope: { author: string }; payload: Payload };
      if (payload.type === 'device.add') this.#serial(() => this.#reconcile(conv));
      if (payload.type === 'group.invite' && conv.kind === 'direct') this.#serial(() => this.#onGroupInvite(conv, envelope.author, payload));
      if (payload.type === 'member.leave' && conv.kind === 'group') this.#serial(() => this.#onLeave(conv, payload.person));
      if (payload.type === 'device.revoke' && conv.kind === 'group') this.#serial(() => this.#finishLeaving(conv));
    });
    if (this.#opts.syncFor) conv.sync = this.#opts.syncFor(conv);
    this.#changed();
    return conv;
  }

  /** Announces (or re-announces with every epoch) a conversation in my space. */
  async #announce(conv: Conversation): Promise<void> {
    await this.space.journal.append({
      type: 'conv.add',
      ts: Date.now(),
      conv: conv.id,
      secrets: structuredClone(conv.journal.space),
      peer: conv.peer ?? undefined,
      kind: conv.kind,
      name: conv.kind === 'group' ? conv.title() : undefined,
    });
    await this.space.convsSettled();
  }

  /**
   * Adds to the conversation every active device of mine it does not have yet, and my current
   * name if the conversation has an older one. Only a name change NEWER than what the
   * conversation holds is written: a device lagging behind never restores an old name.
   */
  async #reconcile(conv: Conversation): Promise<void> {
    if (!conv.journal.isMember) return;
    await conv.settled();
    const mine = conv.profiles.get(this.identity.pub);
    const profile = this.space.profile;
    if (!mine ? true : profile.ts > mine.ts && profile.name !== mine.name) {
      await conv.journal.append({ type: 'profile.set', ts: Date.now(), name: this.space.displayName });
    }
    for (const m of this.space.journal.activeMembers()) {
      if (!conv.journal.members.has(m.record.id)) {
        await conv.journal.append({ type: 'device.add', ts: Date.now(), device: await attest(this.identity, publicRecord(m.record)) });
      }
    }
  }
}

function deleteDb(name: string): Promise<void> {
  return new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = req.onerror = req.onblocked = () => resolve();
  });
}
